/**
 * Search Response Parser Utility
 *
 * Handles parsing of the nested JSON response structure from TEFAS GetAllFunds API.
 * The API returns a double-encoded JSON structure that requires special handling.
 */

import { SearchResult, FundType } from '../types';
import { ValidationError } from '../errors';

/**
 * Raw search API response structure from TEFAS GetAllFunds endpoint
 * @internal
 */
interface SearchApiResponse {
  /** Array of JSON-encoded strings */
  d: string[];
}

/**
 * Parsed fund entry from nested JSON string
 * @internal
 */
interface ParsedFundEntry {
  /** Full display name: "{CODE} - {FULL_NAME}" (code will be extracted and removed) */
  First: string;
  /** Fund code (e.g., "IIH") */
  Second: string;
}

/**
 * Parse TEFAS search API response into SearchResult array
 *
 * The TEFAS GetAllFunds API returns a nested JSON structure:
 * 1. Top level has a "d" array property
 * 2. Each array element is a JSON-encoded string (double-encoded)
 * 3. Each string contains an object with "First" (display name) and "Second" (fund code)
 *
 * @param response - Raw API response object
 * @param fundType - Fund type to include in each result
 * @returns Array of SearchResult objects
 * @throws ValidationError if response structure is invalid
 *
 * @example
 * ```typescript
 * const apiResponse = {
 *   d: [
 *     '{"First":"IIH - İSTANBUL PORTFÖY ÜÇÜNCÜ HİSSE SENEDİ FONU","Second":"IIH"}'
 *   ]
 * };
 * const results = parseSearchResponse(apiResponse, FundType.YAT);
 * // results = [{ fundCode: "IIH", fundName: "İSTANBUL PORTFÖY ÜÇÜNCÜ HİSSE SENEDİ FONU", fundType: FundType.YAT }]
 * ```
 */
export function parseSearchResponse(response: unknown, fundType: FundType): SearchResult[] {
  // Validate response structure
  if (!response || typeof response !== 'object') {
    throw new ValidationError('Invalid search response: response must be an object');
  }

  const apiResponse = response as SearchApiResponse;

  if (!apiResponse.d || !Array.isArray(apiResponse.d)) {
    throw new ValidationError('Invalid search response: missing or invalid "d" array');
  }

  // Handle empty results
  if (apiResponse.d.length === 0) {
    return [];
  }

  // Parse each JSON string in the array
  const results: SearchResult[] = [];

  for (let i = 0; i < apiResponse.d.length; i++) {
    const item = apiResponse.d[i];

    // Validate item is a string
    if (typeof item !== 'string') {
      throw new ValidationError(
        `Invalid search response: item at index ${i} is not a string`
      );
    }

    // Skip empty strings
    if (item.trim().length === 0) {
      continue;
    }

    try {
      // Parse the nested JSON string
      const parsed = JSON.parse(item) as ParsedFundEntry;

      // Validate parsed structure
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Parsed item is not an object');
      }

      if (typeof parsed.First !== 'string' || typeof parsed.Second !== 'string') {
        throw new Error('Missing or invalid First/Second properties');
      }

      // Extract full name from display format "{CODE} - {FULL_NAME}"
      const fullName = parsed.First.includes(' - ') 
        ? parsed.First.split(' - ').slice(1).join(' - ') 
        : parsed.First;

      // Map to SearchResult
      results.push({
        fundCode: parsed.Second,
        fundName: fullName,
        fundType: fundType
      });
    } catch (error) {
      // Provide clear error message for malformed JSON
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new ValidationError(
        `Invalid search response: failed to parse item at index ${i}: ${errorMessage}`,
        error
      );
    }
  }

  return results;
}

/**
 * Validate that a search response has the expected structure
 *
 * @param response - Response to validate
 * @returns True if response has valid structure, false otherwise
 *
 * @example
 * ```typescript
 * if (isValidSearchResponse(apiResponse)) {
 *   const results = parseSearchResponse(apiResponse, FundType.YAT);
 * }
 * ```
 */
export function isValidSearchResponse(response: unknown): boolean {
  try {
    if (!response || typeof response !== 'object') {
      return false;
    }

    const apiResponse = response as SearchApiResponse;

    if (!apiResponse.d || !Array.isArray(apiResponse.d)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}
